import { SvgIcon } from "@progress/kendo-react-common";
import type { SVGIcon } from "@progress/kendo-svg-icons";
import {
    clipboardTextIcon,
    lightbulbIcon,
    warningTriangleIcon
} from "@progress/kendo-svg-icons";
import { useMemo } from "react";
import type { InsightItem } from "../../../models/commericalPlanModels";

type CommercialPlanInsightType = "reasoning" | "caveat" | "suggestion";

type CommercialPlanInsight = {
    title: string;
    description: string;
    icon: SVGIcon;
    type: CommercialPlanInsightType;
};

type CommercialPlanInsightSection = {
    type: CommercialPlanInsightType;
    items: InsightItem[];
    icon: SVGIcon;
    emptyTitle: string;
    emptyDescription: string;
};

type CommercialPlanInsightsProps = {
    planReasoning: InsightItem[];
    caveats: InsightItem[];
    suggestions: InsightItem[];
};

const mapInsightSection = (section: CommercialPlanInsightSection): CommercialPlanInsight[] => {
    if (section.items.length === 0) {
        return [
            {
                title: section.emptyTitle,
                description: section.emptyDescription,
                icon: section.icon,
                type: section.type
            }
        ];
    }

    return section.items.map(item => ({
        title: item.title,
        description: item.content,
        icon: section.icon,
        type: section.type
    }));
};

const getInsightIconClassName = (type: CommercialPlanInsightType): string => {
    switch (type) {
        case "caveat":
            return "text-warning bg-warning-subtle";
        case "suggestion":
            return "text-primary bg-primary-subtle";
        case "reasoning":
        default:
            return "text-success bg-success-subtle";
    }
};

const CommercialPlanInsights: React.FC<CommercialPlanInsightsProps> = ({ planReasoning, caveats, suggestions }) => {
    const insights = useMemo<CommercialPlanInsight[]>(() => [
        ...mapInsightSection({
            type: "reasoning",
            items: planReasoning,
            icon: clipboardTextIcon,
            emptyTitle: "Plan Reasoning",
            emptyDescription: "No reasoning provided."
        }),
        ...mapInsightSection({
            type: "caveat",
            items: caveats,
            icon: warningTriangleIcon,
            emptyTitle: "Caveats",
            emptyDescription: "No caveats provided."
        }),
        ...mapInsightSection({
            type: "suggestion",
            items: suggestions,
            icon: lightbulbIcon,
            emptyTitle: "Suggestions",
            emptyDescription: "No suggestions provided."
        })
    ], [planReasoning, caveats, suggestions]);

    return (
        <section className="border rounded-3 shadow-sm bg-white overflow-hidden">
            <div className="d-flex flex-column flex-md-row justify-content-between gap-3 px-3 px-lg-4 py-3 border-bottom bg-light-subtle">
                <div className="d-flex align-items-start gap-2">
                    <div>
                        <h2 className="h5 fw-bold text-body mb-1">AI Reasoning</h2>
                        <p className="small text-muted mb-0">Signals, constraints and opportunities influencing this recommendation.</p>
                    </div>
                </div>
            </div>

            <div className="px-3 px-lg-4 py-4 k-bg-brand-teal-light">
                <div className="row g-3">
                    {insights.map((insight, index) => (
                        <div key={`${insight.type}-${index}-${insight.title}`} className="col-12 col-xl-6">
                            <div className="border rounded-3 h-100 p-3 bg-white border-custom-hover">
                                <div className="d-flex align-items-start gap-3">
                                    <div
                                        className={`rounded-2 d-inline-flex align-items-center justify-content-center flex-shrink-0 ${getInsightIconClassName(insight.type)}`}
                                        style={{ width: 36, height: 36 }}
                                    >
                                        <SvgIcon icon={insight.icon} size="medium" />
                                    </div>
                                    <div>
                                        <h3 className="h6 fw-semibold text-body mb-2">{insight.title}</h3>
                                        <p className="small text-secondary mb-0 lh-lg">{insight.description}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
};

export default CommercialPlanInsights;
